import { useCallback, useLayoutEffect, useState } from 'react';
import { isApproximatelyEqual } from './utility';

/**
 * Custom hook to manage the scroll state of a carousel component.
 *
 * @param {React.RefObject<HTMLDivElement>} containerRef - Ref to the carousel container element.
 * @param {number} amountOfItems - A count of items in the carousel.
 * @returns {object} - An object containing scroll state and a scroll handler.
 */
const useCarouselScrollState = (containerRef: React.RefObject<HTMLDivElement | null>, amountOfItems: number) => {
	const [canScroll, setCanScroll] = useState(false);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(true);

	const updateScrollState = useCallback(() => {
		const slider = containerRef.current;
		if (!slider) return;

		const nextCanScroll = slider.clientWidth < slider.scrollWidth;
		setCanScroll(nextCanScroll);
		setCanScrollLeft(!isApproximatelyEqual(slider.scrollLeft, 0, 0.5) && nextCanScroll);
		setCanScrollRight(
			!isApproximatelyEqual(slider.clientWidth + slider.scrollLeft, slider.scrollWidth, 0.5) && nextCanScroll
		);
		// The warning is generally safe to ignore in this case (because useRef is stable by default)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleScroll = useCallback(() => {
		updateScrollState();
	}, [updateScrollState]);

	useLayoutEffect(() => {
		updateScrollState();
	}, [amountOfItems, updateScrollState]);

	return {
		canScroll,
		canScrollLeft,
		canScrollRight,
		handleScroll
	};
};

export default useCarouselScrollState;
